import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Users, Video } from 'lucide-react';
import { contactsService, meetingsService } from '../services/supabaseService';
import { mockContacts } from '../data/mockData';
import authService from '../services/authService';
import { useNotifications } from '../hooks/useNotifications';

interface MeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (meetingData: any) => void;
  meeting?: any;
  preselectedCompany?: any;
  preselectedUserId?: string; // Para agenda pública
}

const MeetingModal: React.FC<MeetingModalProps> = ({ isOpen, onClose, onSave, meeting, preselectedCompany, preselectedUserId }) => {
  const { notifyMeetingScheduled } = useNotifications();
  const [formData, setFormData] = useState({
    title: '',
    datetime: '',
    duration: 60,
    contactId: '',
    leadId: '',
    contactType: 'contact' as 'contact' | 'lead',
    description: '',
    participants: '',
    status: 'programada',
    assignedUserId: ''
  });
  const [availableContacts, setAvailableContacts] = useState<any[]>([]);
  const [availableLeads, setAvailableLeads] = useState<any[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (import.meta.env.DEV) console.log('🔄 MeetingModal useEffect triggered, isOpen:', isOpen);
    if (isOpen) {
      if (import.meta.env.DEV) console.log('✅ Modal abierto, iniciando carga de datos...');
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    if (import.meta.env.DEV) console.log('🔄 Iniciando carga completa de datos...');
    await Promise.all([
      loadContacts(),
      loadLeads(),
      loadUsers()
    ]);
    if (import.meta.env.DEV) console.log('✅ Carga completa de datos terminada');
  };

  const loadContacts = async () => {
    try {
      setLoadingContacts(true);
      if (import.meta.env.DEV) console.log('🔄 Cargando contactos para modal de reunión...');

      // Load contacts with error handling
      let contactsData = [];
      try {
        contactsData = await contactsService.getAll();
        if (import.meta.env.DEV) console.log('✅ Contactos cargados desde Supabase:', contactsData.length);
      } catch (error) {
        console.error('❌ Error cargando contactos desde Supabase:', error);
        if (import.meta.env.DEV) console.log('🔄 Usando datos mock como fallback');
        contactsData = mockContacts;
      }

      if (import.meta.env.DEV) console.log('✅ Contactos cargados:', contactsData.length);
      if (contactsData.length > 0) {
        if (import.meta.env.DEV) console.log('📋 Primer contacto:', contactsData[0]);
      }
      setAvailableContacts(contactsData);
    } catch (error) {
      console.error('❌ Error en loadContacts:', error);
      setAvailableContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadLeads = async () => {
    try {
      if (import.meta.env.DEV) console.log('🔄 Cargando leads para modal de reunión...');
      const { leadsService } = await import('../services/supabaseService');
      const leadsData = await leadsService.getAll();
      if (import.meta.env.DEV) console.log('✅ Leads cargados desde Supabase:', leadsData.length);
      if (leadsData.length > 0) {
        if (import.meta.env.DEV) console.log('📋 Primer lead:', leadsData[0]);
      }
      setAvailableLeads(leadsData);
    } catch (error) {
      console.error('❌ Error cargando leads:', error);
      setAvailableLeads([]);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      if (import.meta.env.DEV) console.log('🔄 Cargando usuarios para modal de reunión...');
      const { usersService } = await import('../services/supabaseService');
      const users = await usersService.getAll();
      if (import.meta.env.DEV) console.log('📊 Total usuarios cargados desde base de datos:', users.length);
      if (import.meta.env.DEV) console.log('📋 Usuarios cargados:', users.map(u => ({ id: u.id, name: u.name, email: u.email, role: u.role, isActive: u.isActive })));

      const activeUsers = users.filter(user => user.isActive);
      if (import.meta.env.DEV) console.log('✅ Usuarios activos filtrados:', activeUsers.length);

      if (activeUsers.length === 0) {
        console.warn('⚠️ No se encontraron usuarios activos. Usuarios totales:', users.length);
      }

      setAvailableUsers(activeUsers);

      // Set default assigned user
      if (preselectedUserId) {
        if (import.meta.env.DEV) console.log('👤 Usando usuario preseleccionado:', preselectedUserId);
        setFormData(prev => ({ ...prev, assignedUserId: preselectedUserId }));
      } else if (activeUsers.length > 0) {
        // Default to superadmin if available
        const superadmin = activeUsers.find(u => u.role === 'superadmin');
        const defaultUserId = superadmin?.id || activeUsers[0].id;
        if (import.meta.env.DEV) console.log('👤 Usando usuario por defecto:', defaultUserId, 'de', activeUsers.length, 'usuarios activos');
        setFormData(prev => ({
          ...prev,
          assignedUserId: defaultUserId
        }));
      } else {
        console.warn('⚠️ No hay usuarios activos disponibles para asignar');
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Get current user for default owner
  const currentSession = authService.getCurrentSession();
  const currentUser = currentSession?.user;

  // Effect to handle preselected company
  React.useEffect(() => {
    if (preselectedCompany && isOpen) {
      // Find a contact from the preselected company
      const companyContacts = availableContacts.filter(contact => 
        preselectedCompany.contacts.includes(contact.id)
      );
      
      setFormData(prev => ({
        ...prev,
        title: `Reunión con ${preselectedCompany.name}`,
        contactId: companyContacts.length > 0 ? companyContacts[0].id : '',
        participants: preselectedCompany.email
      }));
    }
  }, [preselectedCompany, isOpen, availableContacts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const meetingDateTime = new Date(formData.datetime);
    const now = new Date();

    if (meetingDateTime <= now) {
      alert('La fecha de la reunión debe ser en el futuro');
      return;
    }

    if (!formData.assignedUserId) {
      alert('Debes asignar la reunión a un usuario');
      return;
    }

    let contact = null;
    let lead = null;

    // Load contact or lead based on selection
    if (formData.contactType === 'contact' && formData.contactId) {
      try {
        contact = await contactsService.getById(formData.contactId);
        if (import.meta.env.DEV) console.log('📋 Contacto cargado para reunión:', contact);
      } catch (error) {
        console.error('Error loading contact:', error);
      }
    } else if (formData.contactType === 'lead' && formData.leadId) {
      try {
        const { leadsService } = await import('../services/supabaseService');
        const leadData = await leadsService.getAll();
        lead = leadData.find(l => l.id === formData.leadId);
        if (import.meta.env.DEV) console.log('📋 Lead cargado para reunión:', lead);
      } catch (error) {
        console.error('Error loading lead:', error);
      }
    }

    const selectedEntity = contact || lead;

    // Google Calendar API integration eliminada (Calendly ya cubre bookings
    // públicos y los meetings internos guardan un meetLink manual). Antes
    // este bloque intentaba crear el evento Google + Meet, con múltiples
    // ramas de error y sincronía antiguas. Simplificado a un único save
    // sin dependencias externas.
    try {
      await meetingsService.create({
        title: formData.title,
        datetime: new Date(formData.datetime),
        duration: formData.duration,
        participants: formData.participants ? formData.participants.split(',').map(p => p.trim()) : [],
        description: formData.description || '',
        status: 'programada',
        meetLink: (formData as any).meetLink || undefined,
        contactId: formData.contactType === 'contact' ? formData.contactId : null,
        owner: currentUser?.name || '',
        assignedUserId: formData.assignedUserId,
        createdBy: currentUser?.id || null,
        approvalStatus: 'approved', // Internal meetings are pre-approved
        clientEmail: selectedEntity?.email,
        clientName: selectedEntity?.fullName
      });

      await notifyMeetingScheduled(
        formData.title,
        selectedEntity?.fullName || 'Cliente',
        formData.assignedUserId
      );

      if (import.meta.env.DEV) console.log('✅ Reunión guardada en Supabase correctamente');
      onSave(formData);
    } catch (error) {
      console.error('Error saving meeting to database:', error);
      alert('Error al guardar la reunión en la base de datos');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {meeting ? 'Editar Reunión' : 'Nueva Reunión'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título de la reunión *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="ej: Reunión inicial con cliente"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha y hora *
              </label>
              <input
                type="datetime-local"
                required
                value={formData.datetime}
                onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duración (minutos)
              </label>
              <select
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={30}>30 minutos</option>
                <option value={60}>1 hora</option>
                <option value={90}>1.5 horas</option>
                <option value={120}>2 horas</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contacto asociado
            </label>
            <select
              value={
                formData.contactType === 'contact' && formData.contactId
                  ? `contact:${formData.contactId}`
                  : formData.contactType === 'lead' && formData.leadId
                  ? `lead:${formData.leadId}`
                  : ''
              }
              onChange={(e) => {
                const value = e.target.value;
                if (import.meta.env.DEV) console.log('📝 Selección de contacto/lead cambiada:', value);

                if (!value) {
                  setFormData({
                    ...formData,
                    contactId: '',
                    leadId: '',
                    contactType: 'contact'
                  });
                  return;
                }

                const [type, id] = value.split(':');
                if (import.meta.env.DEV) console.log('📋 Tipo:', type, 'ID:', id);

                if (type === 'contact') {
                  setFormData({
                    ...formData,
                    contactId: id,
                    leadId: '',
                    contactType: 'contact'
                  });
                  if (import.meta.env.DEV) console.log('✅ Contacto seleccionado:', id);
                } else if (type === 'lead') {
                  setFormData({
                    ...formData,
                    contactId: '',
                    leadId: id,
                    contactType: 'lead'
                  });
                  if (import.meta.env.DEV) console.log('✅ Lead seleccionado:', id);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loadingContacts}
            >
              <option value="">{loadingContacts ? 'Cargando...' : 'Seleccionar contacto o lead'}</option>

              {/* Contacts Section */}
              {availableContacts.length > 0 && (
                <optgroup label="Contactos">
                  {(preselectedCompany
                    ? availableContacts.filter(contact => preselectedCompany.contacts.includes(contact.id))
                    : availableContacts
                  ).map(contact => (
                    <option key={`contact-${contact.id}`} value={`contact:${contact.id}`}>
                      {contact.fullName} - {contact.company} (Contacto)
                    </option>
                  ))}
                </optgroup>
              )}

              {/* Leads Section */}
              {availableLeads.length > 0 && (
                <optgroup label="Leads">
                  {availableLeads.map(lead => (
                    <option key={`lead-${lead.id}`} value={`lead:${lead.id}`}>
                      {lead.fullName} - {lead.company} (Lead)
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {preselectedCompany && (
              <p className="text-xs text-gray-500 mt-1">
                Mostrando solo contactos de {preselectedCompany.name}
              </p>
            )}
            {!loadingContacts && availableContacts.length === 0 && availableLeads.length === 0 && (
              <p className="text-xs text-orange-600 mt-1">
                No hay contactos ni leads disponibles. Crea uno primero.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Participantes adicionales
            </label>
            <input
              type="text"
              value={formData.participants}
              onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="ej: ana@empresa.com, carlos@empresa.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Separa múltiples emails con comas
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe el objetivo de la reunión..."
            />
          </div>

          {/* Calendly info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-1">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Calendly</span>
            </div>
            <p className="text-xs text-blue-700">
              Las reuniones agendadas via Calendly se sincronizan automáticamente con el CRM.
              Aquí puedes crear reuniones manuales adicionales.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asignar a usuario
            </label>
            <select
              value={formData.assignedUserId}
              onChange={(e) => setFormData({ ...formData, assignedUserId: e.target.value })}
              disabled={loadingUsers}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{loadingUsers ? 'Cargando usuarios...' : 'Seleccionar usuario'}</option>
              {availableUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} - {user.email} ({user.role === 'superadmin' ? 'Superadmin' : user.role === 'admin' ? 'Admin' : 'Usuario'})
                </option>
              ))}
            </select>
            {preselectedUserId && (
              <p className="text-xs text-gray-500 mt-1">
                Usuario preseleccionado desde agenda pública
              </p>
            )}
            {!loadingUsers && availableUsers.length === 0 && (
              <p className="text-xs text-red-600 mt-1">
                No hay usuarios activos disponibles. Contacta al administrador.
              </p>
            )}
            {!loadingUsers && availableUsers.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {availableUsers.length} usuario{availableUsers.length !== 1 ? 's' : ''} activo{availableUsers.length !== 1 ? 's' : ''} disponible{availableUsers.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {meeting ? 'Actualizar' : 'Crear'} Reunión
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MeetingModal;